import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, MapPin, Github, Linkedin, Download } from "lucide-react";
import { profile, contactInterests } from "@/data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-cyan-400/60 focus:bg-white/10 focus:glow-cyan";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", interests: [] });
  const [sending, setSending] = useState(false);

  const toggleInterest = (i) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — I'll get back to you within 24h.");
      setForm({ name: "", email: "", subject: "", message: "", interests: [] });
    } catch {
      toast.error("Something went wrong. Please email me directly.");
    } finally {
      setSending(false);
    }
  };

  const socials = [
    { icon: Github, label: "GitHub", href: profile.socials.github, testid: "contact-github-link" },
    { icon: Linkedin, label: "LinkedIn", href: profile.socials.linkedin, testid: "contact-linkedin-link" },
  ];

  return (
    <section id="contact" className="relative py-24 lg:py-36" data-testid="contact-section">
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full orb-violet" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 08 — Signal Me"}
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Let's build something<br /><span className="gradient-text">statistically significant</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="card-glass rounded-2xl border border-white/10 p-6 sm:p-8 lg:col-span-7"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="contact-name-input"
                required
                minLength={2}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                placeholder="Your email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <input
              data-testid="contact-subject-input"
              required
              minLength={2}
              placeholder="Subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={`${inputCls} mt-5`}
            />
            <textarea
              data-testid="contact-message-input"
              required
              minLength={5}
              rows={5}
              placeholder="Tell me about the opportunity, the data, the problem..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} mt-5 resize-none`}
            />
            <div className="mt-5 flex flex-wrap gap-2" data-testid="contact-interest-options">
              {contactInterests.map((i) => (
                <button
                  type="button"
                  key={i}
                  data-testid={`contact-interest-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => toggleInterest(i)}
                  className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all duration-300 ${
                    form.interests.includes(i)
                      ? "border border-cyan-400/60 bg-cyan-500/15 text-cyan-300"
                      : "border border-white/10 text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {i}
                </button>
              ))}
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 px-7 py-4 font-mono text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:opacity-90 hover:glow-violet disabled:opacity-50 sm:w-auto"
            >
              {sending ? "Transmitting..." : "Send Message"} <Send size={14} />
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-6 lg:col-span-5"
          >
            <div className="card-glass rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-cyan-400" />
                <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Email</span>
              </div>
              <a
                data-testid="contact-email-link"
                href={`mailto:${profile.email}`}
                className="mt-2 block font-display text-lg font-semibold text-slate-100 transition-colors hover:text-cyan-300"
              >
                {profile.email}
              </a>
              <div className="mt-5 flex items-center gap-3">
                <MapPin size={16} className="text-violet-400" />
                <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Based in</span>
              </div>
              <p className="mt-2 text-slate-200" data-testid="contact-location">{profile.location}</p>
            </div>

            <div className="card-glass rounded-2xl border border-white/10 p-6">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Elsewhere</span>
              <div className="mt-4 space-y-3">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    data-testid={s.testid}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between rounded-lg border border-white/10 px-4 py-3 transition-all duration-300 hover:border-cyan-400/40"
                  >
                    <span className="flex items-center gap-3 text-sm text-slate-300">
                      <s.icon size={15} className="text-slate-500 transition-colors group-hover:text-cyan-400" />
                      {s.label}
                    </span>
                    <span className="font-mono text-[10px] text-slate-600 transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              data-testid="contact-download-cv-button"
              href={profile.cvUrl}
              download
              onClick={() => toast.success("CV download started.")}
              className="flex items-center justify-center gap-3 rounded-2xl border border-violet-500/40 bg-violet-500/10 p-6 font-mono text-xs font-bold uppercase tracking-widest text-violet-300 transition-all duration-300 hover:bg-violet-500/20 hover:glow-violet"
            >
              <Download size={16} /> Download full CV (PDF)
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
